/**
 * WHERE A NOTIFICATION TAKES YOU — the whole journey, not the doorstep.
 *
 * This used to live inside App as a closure, which meant only a click on the
 * bell could use it. A link arriving from an e-mail or a browser push carries
 * the same three facts (kind, ref, tab) and deserves the same arrival, so the
 * rules moved here and both doors call them:
 *
 *  · An admin alert lands on its PANEL (the feedback itself), not on the
 *    Administration home for you to go hunting.
 *  · A client's case NEVER throws you out of the portal. Documents landed in
 *    Parallel? You go to the CASE, on its documents module.
 *  · The case page opens on the module the news is about: the task list for a
 *    task, notes for a note, documents for documents.
 */
import { setAdminFocus, setTicketSection } from '@/hooks/useDeepLink';
import { setTicketFocus, setTaskFocus, setCommentFocus } from '@/hooks/useWorkFocus';
import { setMyWorkTab } from '@/hooks/useMyWorkTab';
import { setHandoverFocus } from '@/hooks/useHandoverFocus';
import { setActesFocus } from '@/lib/actesFocus';
import { replaceWithin } from '@/lib/navigator';

export interface RoutableNotif {
  kind: string;
  refId?: string | null;
  target?: string | null;
  tab?: string | null;
  /** Actes only: which of its tabs, when the link named one. */
  actesTab?: string | null;
}

/** Super-admin business — [Administration tab, Portal-management page]. */
const ADMIN_AT: Record<string, [string, string?]> = {
  feedback: ['petitions', 'feedback'],
  'resource-request': ['petitions', 'resources'],
  'link-request': ['petitions', 'requests'],
  access: ['users'],
  'app-error': ['errors'],
};
/** Which module of the case page the news is about. */
const SECTION_OF: Record<string, string> = {
  'docs-pending': 'docs', 'docs-reminder': 'docs',
  note: 'notes', 'note-tag': 'notes', 'note-update': 'notes',
  assigned: 'tasks', collab: 'tasks', reminder: 'tasks',
  'task-started': 'tasks', 'task-status': 'tasks',
  'case-date': 'cal', 'case-reminder-set': 'cal', 'ticket-stage': 'activity',
  'task-file': 'tasks', 'task-comment': 'tasks',
};
const ON_CASE = [
  'ticket-back',
  'ticket-new', 'ticket-stage', 'note', 'note-tag', 'note-update',
  'docs-pending', 'docs-reminder', 'case-date',
  /* LE NOM QUE LE PRODUCTEUR ÉCRIT VRAIMENT.
     La liste disait « case-reminder » ; le serveur envoie
     « case-reminder-set » (case-reminder.controller.ts). Le rappel posé sur un
     dossier ne correspondait donc à rien et retombait sur l'onglet générique.
     Vérifié en comparant CE fichier avec tous les `kind:` du serveur. */
  'case-reminder-set',
];
/* « task-file » — un fichier déposé sur une tâche. Il portait bien l'id de la
   tâche et l'onglet, mais n'était dans aucune liste : on arrivait sur les
   tâches, sans ouvrir la tâche. */
const ON_TASK = ['assigned', 'collab', 'reminder', 'task-started', 'task-status', 'waiting-on-you', 'task-file'];
const ON_ACTES = ['actes-demande', 'actes-expiry', 'actes-digest', 'actes-intake', 'actes-assigned'];

/**
 * Do the travelling. `setSection` is the only thing the caller owns — every
 * other destination is a module store, which is what lets an e-mail link and
 * a bell click end in exactly the same place.
 */
export function routeNotification(n: RoutableNotif, setSection: (s: string) => void): void {
  const at = ADMIN_AT[n.kind];
  if (at) { setAdminFocus(at[0], at[1], n.refId || null); setSection('admin'); return; }
  if (n.target === 'tools') { setSection('tools'); return; }
  if (n.target === 'planner') { setSection('planner'); return; }
  /* « Votre ressource est publiée » visait `documents`, une section qui existe
     bel et bien — mais que ce routeur ne connaissait pas, et la nouvelle
     finissait sur l'espace de travail. */
  if (n.target === 'documents') { setSection('documents'); return; }

  /* Every Actes news goes to Actes — the list named five kinds and the
     rest (assignment, dpc_due, acte_change…) fell through to My work. */
  if (ON_ACTES.includes(n.kind) || n.kind.startsWith('actes-') || n.target === 'actes') {
    /* THE REF IS A DOSSIER OR A DEMANDE, and the tab says which (the mirror
       trigger writes dossier_id or acte_request_id, and tab 'dossiers' or
       'demandes'). A demande's id was opened AS A DOSSIER — an empty,
       broken-looking page (Gerónimo, 24 Sept). A dossier opens itself; a
       demande opens the Demandes tab. */
    const tab = n.actesTab || n.tab || null;
    if (tab === 'demandes') setActesFocus(n.refId || null, 'demandes');
    else if (tab === 'board' || !n.refId) setActesFocus(null, tab);
    else setActesFocus(n.refId, tab || 'dossiers');
    setSection('actes');
    return;
  }
  /* A HANDOVER NEWS OPENS THE HANDOVER — on the Handover tab. It used to set
     the handover to open and then fall through to «whatever tab the
     notification named», which for most rows was none: you landed on the
     tab you had left, with the handover waiting unseen in another one
     (Gerónimo, 24 Sept). */
  const HO_KINDS = ['handover', 'ho-reminder', 'ho-mention', 'ho-team', 'ho-alert'];
  if (HO_KINDS.includes(n.kind)) {
    // ho-alert refIds are "<handover>:<ticket>:<date>" — the handover is the door.
    const hid = n.refId ? (n.kind === 'ho-alert' ? n.refId.split(':')[0] : n.refId) : null;
    if (hid) setHandoverFocus(hid);
    setMyWorkTab('handover', { pulse: false });
    setSection('mywork');
    return;
  }

  if (n.refId && ON_CASE.includes(n.kind)) {
    /* A destination, not a tab tap: pulsing here would ask the case we are
       about to open to close itself. */
    setMyWorkTab('tickets', { pulse: false });
    setTicketFocus(n.refId);
    const sec = SECTION_OF[n.kind];
    if (sec) setTicketSection(sec);
    setSection('mywork');
    return;
  }
  /**
   * UNE TÂCHE DÉLÉGUÉE N'EMPORTE PAS TOUJOURS UN ID DE TÂCHE.
   *
   * Le producteur envoie délibérément le DOSSIER dans `refId` pour « assigned »
   * et « collab » — semer un dossier crée quinze tâches d'un coup, et quinze
   * lignes disant la même chose sont du papier peint ; avec le dossier, l'écran
   * les regroupe en « N nouvelles tâches sur X ». Il ne retombe sur l'id de la
   * tâche que lorsqu'il n'y a pas de dossier.
   *
   * Les deux lecteurs — celui-ci et le constructeur de liens des e-mails —
   * croyaient pourtant tenir un id de TÂCHE, et appelaient `setTaskFocus()`
   * avec un numéro de dossier : il ne correspondait à rien, aucune tâche ne
   * s'ouvrait, et on atterrissait sur l'onglet qu'on avait laissé la veille.
   *
   * On tranche par la FORME, qui ne ment pas : un uuid est une tâche, un
   * numéro HubSpot est un dossier. Ainsi les notifications déjà stockées
   * arrivent au bon endroit sans rien migrer.
   */
  /* A COMMENT ON A TASK: «<task>#<comment>». The task opens, on the comment
     — not on the task list, not on the top of the task. */
  if (n.kind === 'task-comment' && n.refId) {
    const [task, comment] = n.refId.split('#');
    setMyWorkTab('tasks', { pulse: false });
    if (comment) setCommentFocus(comment);
    setTaskFocus(task);
    setSection('mywork');
    return;
  }
  if (n.refId && ON_TASK.includes(n.kind)) {
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(n.refId);
    if (isUuid) {
      /* La tâche elle-même : on l'ouvre, et on va sur l'onglet qui la
         contient — le régler seulement « si la notification l'a dit »
         laissait la tâche mise au point derrière l'onglet d'hier. */
      setMyWorkTab('tasks', { pulse: false });
      setTaskFocus(n.refId);
      setSection('mywork');
      return;
    }
    /* Un dossier : on l'ouvre sur SA LISTE DE TÂCHES, qui est précisément la
       nouvelle qu'on vient d'annoncer. */
    setMyWorkTab('tickets', { pulse: false });
    setTicketFocus(n.refId);
    setTicketSection('tasks');
    setSection('mywork');
    return;
  }
  if (n.tab) setMyWorkTab(n.tab as 'tickets' | 'tasks' | 'notes' | 'handover');
  setSection('mywork');
}

/**
 * The address bar's version of the same thing: `?n=<kind>&ref=<id>&tab=<t>`,
 * written by the server on every e-mail and push link. Read once, applied
 * through the routing above — which lands on a path without the parameters,
 * replacing the link's own entry — so a reload behaves like a normal visit.
 * Returns true when it actually travelled.
 */
let lastConsumed = '';
export function consumeNotifLink(setSection: (s: string) => void): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    const kind = p.get('n');
    const focus = p.get('focus');
    const tab = p.get('tab');
    if (!kind && !focus) return false;
    /* StrictMode runs the effect twice, and the address is not cleaned until
       the router commits. */
    if (lastConsumed === window.location.href) return false;
    lastConsumed = window.location.href;
    replaceWithin(() => {
      /* An Actes link names its tab and, for a dossier, the dossier itself. */
      if (focus || window.location.pathname.replace(/^\/+/, '').split('/')[0] === 'actes') {
        setActesFocus(focus, tab);
        setSection('actes');
        return;
      }
      routeNotification({ kind: kind || '', refId: p.get('ref'), tab, actesTab: tab }, setSection);
    });
    return true;
  } catch { return false; }
}
